'use client';

import { useEffect } from 'react';
import { logClientEvent } from '@/lib/log-client-event';
import { useTranslation } from 'react-i18next';
import {
  PRO_LIST_COMPANIES_LIMIT,
  PRO_LISTS_LIMIT,
} from '@/config/stripe-plans.config';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LocaleLink } from '@/components/locale-link';

export type ListLimitVariant =
  | 'pro_required'
  | 'lists_limit'
  | 'companies_limit';

/**
 * The Lists feature's gate dialog, one component for its three stops:
 * a free account trying to create a list (upsell), a paid account at its
 * list-count cap, and a list at its size cap — a decision point, not a
 * toast.
 *
 * `limit` is the number the server actually enforced (limits are per tier
 * now); the Pro constants remain only as a fallback for callers that
 * didn't get one.
 */
export function ListLimitDialog({
  open,
  onOpenChange,
  variant,
  limit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant: ListLimitVariant;
  limit?: number;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (open && variant === 'pro_required') {
      logClientEvent('paywall_shown', undefined, { kind: 'pro', feature: 'lists' });
    }
  }, [open, variant]);

  const title =
    variant === 'pro_required'
      ? t('lists.limitDialog.proTitle', 'Lists is a Pro feature')
      : variant === 'lists_limit'
        ? t('lists.limitDialog.listsTitle', 'List limit reached')
        : t('lists.limitDialog.companiesTitle', 'This list is full');

  const description =
    variant === 'pro_required'
      ? t(
          'lists.limitDialog.proDescription',
          'Create lists of competitors or portfolio companies, group them into segments, set up change alerts and share them — with FirmenData Pro.',
        )
      : variant === 'lists_limit'
        ? t('lists.limitDialog.listsDescription', {
            count: limit ?? PRO_LISTS_LIMIT,
            defaultValue:
              'Your plan includes {{count}} lists. Delete one you no longer need to make room.',
          })
        : t('lists.limitDialog.companiesDescription', {
            count: limit ?? PRO_LIST_COMPANIES_LIMIT,
            defaultValue:
              'A list can hold {{count}} companies. Remove one from this list to make room.',
          });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.buttons.close', 'Close')}
          </Button>
          {variant === 'pro_required' && (
            <Button asChild>
              <LocaleLink href="/plans" onClick={() => onOpenChange(false)}>
                {t('lists.limitDialog.cta', 'Upgrade to Pro')}
              </LocaleLink>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
